import React, { useState } from 'react';
import { Search, Database, AlertTriangle, CheckCircle, Copy, Download, RefreshCw } from 'lucide-react';
import { useMigrationStatus } from '../../lib/hooks';

interface TableInfo {
  table_name: string;
  column_count: number;
  row_count: number;
  is_primary: boolean;
  status: 'active' | 'legacy' | 'deprecated';
}

const DatabaseReferencePage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDomain, setSelectedDomain] = useState<string>('all');
  
  // Use React Query hook for migration status
  const { data: migrationStatus = [], isLoading: loading, refetch } = useMigrationStatus();

  const domains = [
    { id: 'all', name: 'All Domains' },
    { id: 'customers', name: 'Customer Data' },
    { id: 'calls', name: 'Call Data' },
    { id: 'agreements', name: 'Agreements' },
    { id: 'agents', name: 'Agent Data' },
    { id: 'knowledge', name: 'Knowledge Base' },
  ];

  // Primary table configurations - SINGLE SOURCE OF TRUTH
  const primaryTables = {
    customers: {
      name: 'customers',
      description: 'Single source of truth for all customer data',
      columns: [
        { name: 'id', type: 'uuid', description: 'Primary key' },
        { name: 'first_name', type: 'text', description: 'Customer first name' },
        { name: 'last_name', type: 'text', description: 'Customer last name' },
        { name: 'email', type: 'text', description: 'Customer email' },
        { name: 'phone', type: 'text', description: 'Customer phone number' },
        { name: 'is_cell', type: 'boolean', description: 'Is cell phone' },
        { name: 'comms_opt_in', type: 'boolean', description: 'Communications opt-in status' },
        { name: 'comms_opt_in_date', type: 'timestamptz', description: 'When opted in to communications' },
        { name: 'prefer_sms', type: 'boolean', description: 'Prefers SMS communication' },
        { name: 'prefer_email', type: 'boolean', description: 'Prefers email communication' },
        { name: 'prefer_phone', type: 'boolean', description: 'Prefers phone communication' },
        { name: 'sw_service_account_id', type: 'text', description: 'SuccessWare service account ID' },
        { name: 'sw_customer_id', type: 'text', description: 'SuccessWare customer ID' },
        { name: 'sw_billing_customer_id', type: 'text', description: 'SuccessWare billing customer ID' },
        { name: 'is_member', type: 'boolean', description: 'Has active membership' },
        { name: 'membership_tier', type: 'text', description: 'Membership tier level' },
        { name: 'membership_start_date', type: 'date', description: 'Membership start date' },
        { name: 'created_by_company_id', type: 'text', description: 'Company that created record' },
        { name: 'warranty_status', type: 'text', description: 'Current warranty status' },
        { name: 'service_account_number', type: 'text', description: 'Service account number' },
        { name: 'billing_customer_id', type: 'text', description: 'Billing customer ID' },
        { name: 'pre_contact_source', type: 'text', description: 'Source if from pre_contacts' },
        { name: 'data_source', type: 'text', description: 'Origin of customer data (manual, sw_import, etc)' },
        { name: 'legacy_migrated_at', type: 'timestamptz', description: 'When migrated from legacy table' },
        { name: 'created_at', type: 'timestamptz', description: 'Record creation timestamp' },
        { name: 'updated_at', type: 'timestamptz', description: 'Last update timestamp' },
      ],
      queryExample: `// Correct way to query customers
const { data, error } = await supabase
  .from('customers')
  .select('*')
  .eq('company_id', companyId);

// Search by phone
const { data } = await supabase
  .from('customers')
  .select('*')
  .eq('phone', phoneNumber)
  .single();`,
      antiPatterns: [
        'Do NOT query sw_customers directly - use customers table',
        'Do NOT query warranty_customers directly - use customers table',
        'Do NOT query pre_contacts directly - use customers table',
        'Do NOT query sw_billing_customers directly - use customers table',
        'Do NOT query people table for customer data - use customers table',
        'Always use customers table with appropriate filters for all customer queries',
      ],
      relatedPages: [
        '/customers',
        '/customers/[id]',
        '/contact-center/customer-lookup',
        '/service-titan/customers',
      ],
    },
    calls: {
      name: 'calls',
      description: 'Single source of truth for all call data',
      columns: [
        { name: 'id', type: 'uuid', description: 'Primary key' },
        { name: 'call_sid', type: 'text', description: 'Twilio/Genesys call SID' },
        { name: 'from_number', type: 'text', description: 'Caller phone number' },
        { name: 'to_number', type: 'text', description: 'Destination phone number' },
        { name: 'direction', type: 'text', description: 'inbound or outbound' },
        { name: 'status', type: 'text', description: 'Call status' },
        { name: 'duration_seconds', type: 'integer', description: 'Call duration in seconds' },
        { name: 'recording_url', type: 'text', description: 'URL to call recording' },
        { name: 'agent_id', type: 'uuid', description: 'Agent who handled call' },
        { name: 'customer_id', type: 'uuid', description: 'Customer reference' },
        { name: 'company_id', type: 'text', description: 'Company ID' },
        { name: 'company_name', type: 'text', description: 'Company name' },
        { name: 'call_type', type: 'text', description: 'Type of call' },
        { name: 'metadata', type: 'jsonb', description: 'Additional call metadata' },
        { name: 'started_at', type: 'timestamptz', description: 'When call started' },
        { name: 'ended_at', type: 'timestamptz', description: 'When call ended' },
        { name: 'recording_duration_seconds', type: 'integer', description: 'Recording duration' },
        { name: 'recording_sid', type: 'text', description: 'Recording SID' },
        { name: 'queue_name', type: 'text', description: 'Queue that handled call' },
        { name: 'agent_name', type: 'text', description: 'Agent name (denormalized)' },
        { name: 'agent_email', type: 'text', description: 'Agent email (denormalized)' },
        { name: 'company_brand', type: 'text', description: 'Company brand' },
        { name: 'customer_name', type: 'text', description: 'Customer name (denormalized)' },
        { name: 'is_existing_customer', type: 'boolean', description: 'Whether customer exists' },
        { name: 'successware_customer_id', type: 'text', description: 'SuccessWare customer ID' },
        { name: 'campaign_name', type: 'text', description: 'Marketing campaign' },
        { name: 'genesys_conversation_id', type: 'text', description: 'Genesys conversation ID' },
        { name: 'genesys_recording_id', type: 'text', description: 'Genesys recording ID' },
        { name: 'genesys_queue_id', type: 'text', description: 'Genesys queue ID' },
        { name: 'genesys_queue_name', type: 'text', description: 'Genesys queue name' },
        { name: 'import_source', type: 'text', description: 'Source system (twilio/genesys)' },
        { name: 'is_ai_call', type: 'boolean', description: 'Whether AI handled call' },
        { name: 'sw_call_id', type: 'text', description: 'SuccessWare call ID' },
        { name: 'call_details', type: 'jsonb', description: 'Additional call details' },
        { name: 'legacy_migrated_at', type: 'timestamptz', description: 'When migrated from legacy' },
        { name: 'created_at', type: 'timestamptz', description: 'Record creation timestamp' },
        { name: 'updated_at', type: 'timestamptz', description: 'Last update timestamp' },
      ],
      queryExample: `// Query all calls
const { data } = await supabase
  .from('calls')
  .select('*')
  .eq('company_id', companyId)
  .order('started_at', { ascending: false });

// Query only AI calls
const { data } = await supabase
  .from('calls')
  .select('*')
  .eq('is_ai_call', true)
  .eq('company_id', companyId);`,
      antiPatterns: [
        'Do NOT query ai_calls directly - use calls with is_ai_call=true',
        'Do NOT query sw_calls directly - use calls table',
        'Do NOT query sw_call_details directly - use calls.call_details jsonb column',
        'Do NOT split AI calls and human calls into separate tables',
        'Always use calls table with is_ai_call filter for AI-specific queries',
        'Use import_source field to differentiate Twilio vs Genesys calls',
      ],
      relatedPages: [
        '/calls',
        '/calls/[id]',
        '/ai-voice-agents/calls',
        '/contact-center/active-calls',
        '/analytics/call-metrics',
      ],
    },
    unified_agreements: {
      name: 'unified_agreements',
      description: 'Single source of truth for all agreement types (memberships, warranties, master agreements)',
      columns: [
        { name: 'id', type: 'uuid', description: 'Primary key' },
        { name: 'company_id', type: 'text', description: 'Company reference' },
        { name: 'customer_id', type: 'uuid', description: 'Customer reference' },
        { name: 'agreement_type', type: 'agreement_type enum', description: 'Type: successware, warranty, membership, master' },
        { name: 'source_system_id', type: 'text', description: 'ID from source system (sw_agreement_id, etc)' },
        { name: 'name', type: 'text', description: 'Agreement name' },
        { name: 'status', type: 'text', description: 'Agreement status (active, cancelled, etc)' },
        { name: 'start_date', type: 'date', description: 'Agreement start date' },
        { name: 'end_date', type: 'date', description: 'Agreement end date' },
        { name: 'amount', type: 'numeric', description: 'Agreement amount' },
        { name: 'legacy_migrated_at', type: 'timestamptz', description: 'When migrated from legacy tables' },
        { name: 'created_at', type: 'timestamptz', description: 'Record creation timestamp' },
        { name: 'updated_at', type: 'timestamptz', description: 'Last update timestamp' },
      ],
      queryExample: `// Query all agreements
const { data } = await supabase
  .from('unified_agreements')
  .select('*')
  .eq('company_id', companyId);

// Query only memberships
const { data } = await supabase
  .from('unified_agreements')
  .select('*')
  .eq('agreement_type', 'membership')
  .eq('company_id', companyId);`,
      antiPatterns: [
        'Do NOT query sw_agreements directly',
        'Do NOT query warranty_agreements directly',
        'Do NOT query memberships directly',
        'Do NOT query master_agreements directly',
        'Use unified_agreements with agreement_type filter',
      ],
      relatedPages: [
        '/agreements',
        '/agreements/[id]',
        '/memberships',
        '/warranty/agreements',
      ],
    },
    agents: {
      name: 'agents',
      description: 'Single source of truth for all agent/employee data',
      columns: [
        { name: 'id', type: 'uuid', description: 'Primary key' },
        { name: 'name', type: 'text', description: 'Full agent name' },
        { name: 'email', type: 'text', description: 'Agent email' },
        { name: 'login', type: 'text', description: 'Login username' },
        { name: 'team', type: 'text', description: 'Team assignment' },
        { name: 'role', type: 'text', description: 'Agent role' },
        { name: 'is_active', type: 'boolean', description: 'Whether agent is active' },
        { name: 'current_status', type: 'text', description: 'Current status (online/offline/break/etc)' },
        { name: 'skills', type: 'text[]', description: 'Agent skills (hvac, plumbing, etc)' },
        { name: 'language', type: 'text[]', description: 'Languages spoken' },
        { name: 'total_calls', type: 'integer', description: 'Total calls handled' },
        { name: 'total_talk_time_seconds', type: 'bigint', description: 'Total time on calls' },
        { name: 'user_id', type: 'uuid', description: 'Auth user reference' },
        { name: 'agent_type', type: 'agent_type enum', description: 'Type: call_center, technician, employee, admin' },
        { name: 'sw_employee_id', type: 'text', description: 'SuccessWare employee ID' },
        { name: 'call_center_config', type: 'jsonb', description: 'Call center specific config' },
        { name: 'technician_config', type: 'jsonb', description: 'Technician specific config' },
        { name: 'legacy_migrated_at', type: 'timestamptz', description: 'When migrated from call_center_agents' },
        { name: 'created_at', type: 'timestamptz', description: 'Record creation timestamp' },
        { name: 'updated_at', type: 'timestamptz', description: 'Last update timestamp' },
      ],
      queryExample: `// Query all agents
const { data } = await supabase
  .from('agents')
  .select('*')
  .eq('company_id', companyId);

// Query only call center agents
const { data } = await supabase
  .from('agents')
  .select('*')
  .eq('agent_type', 'call_center')
  .eq('company_id', companyId);`,
      antiPatterns: [
        'Do NOT query call_center_agents directly - use agents with agent_type=call_center',
        'Do NOT query sw_employees directly - use agents table',
        'Do NOT query sw_technicians directly - use agents with agent_type=technician',
        'Do NOT split agent data into separate tables by type',
        'Always use agents table with agent_type filter for role-specific queries',
        'Use call_center_config and technician_config jsonb columns for role-specific data',
      ],
      relatedPages: [
        '/agents',
        '/agents/[id]',
        '/contact-center/agent-console',
        '/service-titan/employees',
      ],
    },
    knowledge_articles: {
      name: 'knowledge_articles',
      description: 'Single source of truth for all knowledge base content',
      columns: [
        { name: 'id', type: 'uuid', description: 'Primary key' },
        { name: 'category_id', type: 'uuid', description: 'Knowledge category reference' },
        { name: 'company_id', type: 'uuid', description: 'Company reference (NULL for global)' },
        { name: 'title', type: 'varchar(255)', description: 'Article title' },
        { name: 'content', type: 'text', description: 'Article content' },
        { name: 'summary', type: 'text', description: 'Brief summary of article' },
        { name: 'keywords', type: 'text[]', description: 'Search keywords' },
        { name: 'tags', type: 'text[]', description: 'Content tags' },
        { name: 'article_type', type: 'varchar(50)', description: 'Article type (default: general)' },
        { name: 'priority', type: 'integer', description: 'Display priority (default: 0)' },
        { name: 'is_active', type: 'boolean', description: 'Whether article is active' },
        { name: 'search_vector', type: 'tsvector', description: 'Full text search vector' },
        { name: 'content_type', type: 'knowledge_content_type enum', description: 'Type: article, qa, faq, script, rule' },
        { name: 'source_system', type: 'text', description: 'Origin of content (manual, import, etc)' },
        { name: 'is_agent_facing', type: 'boolean', description: 'Visible to agents' },
        { name: 'is_customer_facing', type: 'boolean', description: 'Visible to customers' },
        { name: 'legacy_migrated_at', type: 'timestamptz', description: 'When migrated from legacy tables' },
        { name: 'created_at', type: 'timestamptz', description: 'Record creation timestamp' },
        { name: 'updated_at', type: 'timestamptz', description: 'Last update timestamp' },
      ],
      queryExample: `// Query all knowledge articles
const { data } = await supabase
  .from('knowledge_articles')
  .select('*')
  .eq('company_id', companyId)
  .eq('is_agent_facing', true);

// Query only FAQs
const { data } = await supabase
  .from('knowledge_articles')
  .select('*')
  .eq('content_type', 'faq')
  .eq('company_id', companyId);`,
      antiPatterns: [
        'Do NOT query knowledge_qa directly - use knowledge_articles with content_type=qa',
        'Do NOT query agent_knowledge_base directly - use knowledge_articles with is_agent_facing=true',
        'Do NOT query general_knowledge_base directly - use knowledge_articles',
        'Do NOT query agent_faqs directly - use knowledge_articles with content_type=faq',
        'Do NOT query agent_scripts directly - use knowledge_articles with content_type=script',
        'Always use knowledge_articles with content_type and is_agent_facing filters',
        'Query knowledge_categories separately and join on category_id',
      ],
      relatedPages: [
        '/ai-voice-agents/knowledge-base',
        '/contact-center/agent-knowledge',
        '/knowledge/search',
      ],
    },
    call_conversions: {
      name: 'call_conversions',
      description: 'Dedicated table for call conversion tracking',
      columns: [
        { name: 'id', type: 'uuid', description: 'Primary key' },
        { name: 'company_id', type: 'uuid', description: 'Company reference' },
        { name: 'call_id', type: 'uuid', description: 'Call reference' },
        { name: 'customer_id', type: 'uuid', description: 'Customer reference' },
        { name: 'conversion_type', type: 'text', description: 'Type: sale, appointment, callback, quote' },
        { name: 'conversion_value', type: 'decimal', description: 'Monetary value' },
        { name: 'conversion_status', type: 'text', description: 'Status: pending, confirmed, cancelled' },
        { name: 'attributed_to_agent_id', type: 'uuid', description: 'Agent who gets credit' },
      ],
      queryExample: `// Track a conversion
const { data, error } = await supabase
  .from('call_conversions')
  .insert({
    company_id: companyId,
    call_id: callId,
    customer_id: customerId,
    conversion_type: 'sale',
    conversion_value: 1500.00,
    conversion_status: 'pending'
  });

// Query conversions
const { data } = await supabase
  .from('call_conversions')
  .select('*, calls(*), customers(*)')
  .eq('company_id', companyId)
  .gte('converted_at', startDate);`,
      antiPatterns: [
        'Do NOT store conversions in calls.metadata',
        'Do NOT use multiple tables for conversion tracking',
        'Always use call_conversions table',
      ],
      relatedPages: [
        '/contact-center/conversions',
        '/analytics/conversion-metrics',
      ],
    },
  };

  const legacyTables = [
    { name: 'sw_customers', replacement: 'customers', status: '⚠️ CRITICAL: CustomerDetailPage still using directly!' },
    { name: 'warranty_customers', replacement: 'customers', status: 'Use customers table with warranty_status filter' },
    { name: 'pre_contacts', replacement: 'customers', status: '⚠️ CRITICAL: AdminLayout still using directly!' },
    { name: 'sw_billing_customers', replacement: 'customers', status: 'Data synced to customers via triggers' },
    { name: 'people (for customers)', replacement: 'customers', status: 'Use customers table for customer data' },
    { name: 'ai_calls', replacement: 'calls', status: 'Use calls with is_ai_call=true filter' },
    { name: 'sw_calls', replacement: 'calls', status: 'Data synced to calls via triggers' },
    { name: 'sw_call_details', replacement: 'calls.call_details', status: 'Use calls table with call_details jsonb column' },
    { name: 'sw_agreements', replacement: 'unified_agreements', status: 'Data synced to unified_agreements via triggers' },
    { name: 'warranty_agreements', replacement: 'unified_agreements', status: '⚠️ CRITICAL: WarrantyAgreementsPage still using!' },
    { name: 'memberships', replacement: 'unified_agreements', status: 'Some data in unified_agreements table' },
    { name: 'master_agreements', replacement: 'unified_agreements', status: 'Consolidate into unified_agreements' },
    { name: 'call_center_agents', replacement: 'agents', status: '⚠️ CRITICAL: 5 pages + AdminLayout still using!' },
    { name: 'sw_employees', replacement: 'agents', status: 'Data synced to agents via triggers' },
    { name: 'sw_technicians', replacement: 'agents', status: 'Use agents with agent_type=technician' },
    { name: 'knowledge_qa', replacement: 'knowledge_articles', status: 'Use knowledge_articles with content_type=qa' },
    { name: 'agent_knowledge_base', replacement: 'knowledge_articles', status: 'Use knowledge_articles with is_agent_facing=true' },
    { name: 'agent_faqs', replacement: 'knowledge_articles', status: 'Use knowledge_articles with content_type=faq' },
    { name: 'agent_scripts', replacement: 'knowledge_articles', status: 'Use knowledge_articles with content_type=script' },
    { name: 'general_knowledge_base', replacement: 'knowledge_articles', status: 'Use knowledge_articles table' },
  ];

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const exportSchema = () => {
    const schema = JSON.stringify(primaryTables, null, 2);
    const blob = new Blob([schema], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'database-schema.json';
    a.click();
  };

  const filteredTables = Object.entries(primaryTables).filter(([key, table]) => {
    const matchesSearch = table.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      table.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDomain = selectedDomain === 'all' || key.includes(selectedDomain);
    return matchesSearch && matchesDomain;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Database className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Database Reference</h1>
                <p className="text-gray-600">Single source of truth for all database operations</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => refetch()}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
              <button
                onClick={exportSchema}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                <Download className="w-4 h-4" />
                <span>Export Schema</span>
              </button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search tables, columns, or descriptions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={selectedDomain}
              onChange={(e) => setSelectedDomain(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {domains.map((domain) => (
                <option key={domain.id} value={domain.id}>
                  {domain.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Migration Status */}
        {migrationStatus.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Migration Status</h2>
            <div className="space-y-3">
              {migrationStatus.map((status) => (
                <div key={status.domain} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {status.migration_complete ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-yellow-600" />
                    )}
                    <div>
                      <div className="font-medium">{status.domain}</div>
                      <div className="text-sm text-gray-600">
                        {status.legacy_table}: {status.migrated_count} / {status.legacy_count} records migrated
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-medium ${status.migration_complete ? 'text-green-600' : 'text-yellow-600'}`}>
                      {status.migration_complete ? 'Complete' : 'In Progress'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {Math.round((status.migrated_count / status.legacy_count) * 100)}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Primary Tables */}
        <div className="space-y-6">
          {filteredTables.map(([key, table]) => (
            <div key={key} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="bg-blue-600 text-white p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold">{table.name}</h2>
                    <p className="text-blue-100">{table.description}</p>
                  </div>
                  <span className="px-3 py-1 bg-blue-700 rounded-full text-sm font-medium">
                    PRIMARY TABLE
                  </span>
                </div>
              </div>

              <div className="p-6">
                {/* Columns */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Columns</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Column</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Type</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Description</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {table.columns.map((column) => (
                          <tr key={column.name}>
                            <td className="px-4 py-2 text-sm font-mono text-blue-600">{column.name}</td>
                            <td className="px-4 py-2 text-sm text-gray-600">{column.type}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{column.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Query Example */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-900">Query Example</h3>
                    <button
                      onClick={() => copyToClipboard(table.queryExample)}
                      className="flex items-center space-x-1 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
                    >
                      <Copy className="w-4 h-4" />
                      <span>Copy</span>
                    </button>
                  </div>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                    <code>{table.queryExample}</code>
                  </pre>
                </div>

                {/* Anti-Patterns */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">❌ Anti-Patterns (Do NOT do this)</h3>
                  <ul className="space-y-2">
                    {table.antiPatterns.map((pattern, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{pattern}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Related Pages */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Related Pages</h3>
                  <div className="flex flex-wrap gap-2">
                    {table.relatedPages.map((page) => (
                      <span key={page} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-mono">
                        {page}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Legacy Tables Warning */}
        <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6 mt-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-bold text-yellow-900 mb-2">⚠️ Legacy Tables (DO NOT USE)</h3>
              <p className="text-yellow-800 mb-4">
                The following tables are being phased out. Use their replacements instead:
              </p>
              <div className="space-y-2">
                {legacyTables.map((table) => (
                  <div key={table.name} className="flex items-center justify-between p-3 bg-white rounded border border-yellow-300">
                    <div>
                      <span className="font-mono text-red-600 font-medium">{table.name}</span>
                      <span className="text-gray-600 mx-2">→</span>
                      <span className="font-mono text-green-600 font-medium">{table.replacement}</span>
                    </div>
                    <span className="text-sm text-gray-600">{table.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Reference */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-6 mt-6 text-white">
          <h3 className="text-xl font-bold mb-4">📋 Quick Reference</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-blue-800 bg-opacity-50 rounded p-3">
              <div className="font-bold mb-1">Customer Data</div>
              <div className="text-sm text-blue-100">Use: <span className="font-mono">customers</span></div>
            </div>
            <div className="bg-blue-800 bg-opacity-50 rounded p-3">
              <div className="font-bold mb-1">Call Data</div>
              <div className="text-sm text-blue-100">Use: <span className="font-mono">calls</span></div>
            </div>
            <div className="bg-blue-800 bg-opacity-50 rounded p-3">
              <div className="font-bold mb-1">Agreements</div>
              <div className="text-sm text-blue-100">Use: <span className="font-mono">unified_agreements</span></div>
            </div>
            <div className="bg-blue-800 bg-opacity-50 rounded p-3">
              <div className="font-bold mb-1">Agent Data</div>
              <div className="text-sm text-blue-100">Use: <span className="font-mono">agents</span></div>
            </div>
            <div className="bg-blue-800 bg-opacity-50 rounded p-3">
              <div className="font-bold mb-1">Knowledge Base</div>
              <div className="text-sm text-blue-100">Use: <span className="font-mono">knowledge_articles</span></div>
            </div>
            <div className="bg-blue-800 bg-opacity-50 rounded p-3">
              <div className="font-bold mb-1">Conversions</div>
              <div className="text-sm text-blue-100">Use: <span className="font-mono">call_conversions</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatabaseReferencePage;
